// src/store/authStore.ts
import { create } from "zustand";
import { doc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { signOut } from "firebase/auth";
import { useUserStore } from "./userStore";
import { useDebugStore } from "./debugStore";
import { VCardInitializationService } from "../services/vcard/initialization";
import { SVGInitializationService } from "../services/svg/initialization";
import { UserDatabaseStructure } from "../services/database/userStructure";
import type { User, UserProfile } from "../types/user";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const FREE_MEMBERSHIP: UserProfile["membership"] = {
  tier: "free",
  startDate: new Date(),
  autoRenew: false,
  features: {
    maxVCards: 3,
    maxDevices: 3,
    customBranding: false,
    analytics: false,
    bulkGeneration: false,
  },
};

const splitDisplayName = (
  name: string | undefined,
): { firstName: string; lastName: string; fullName: string } => {
  const nameParts = (name || "").trim().split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] || "Unknown";
  const lastName = nameParts.slice(1).join(" ") || "User";
  const fullName = `${firstName} ${lastName}`.trim();

  return { firstName, lastName, fullName };
};

const buildFallbackUserProfile = (
  userId: string,
  user: User,
  existingProfile?: Partial<UserProfile>,
): UserProfile => {
  const authName = user.name || user.displayName;
  const fallbackName = splitDisplayName(authName);
  const firstName = existingProfile?.firstName || fallbackName.firstName;
  const lastName = existingProfile?.lastName || fallbackName.lastName;
  const fullName =
    existingProfile?.fullName ||
    existingProfile?.name ||
    `${firstName} ${lastName}`.trim();

  return {
    ...existingProfile,
    id: userId,
    email: existingProfile?.email || user.email || "",
    firstName,
    lastName,
    fullName,
    name: existingProfile?.name || fullName,
    role: existingProfile?.role || "user",
    membership: existingProfile?.membership || FREE_MEMBERSHIP,
    createdAt: existingProfile?.createdAt || serverTimestamp(),
    updatedAt: existingProfile?.updatedAt || serverTimestamp(),
  };
};

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void; // Sets the current user in the auth store
  logout: () => Promise<void>; // Logs the user out
  loadUserProfile: (userId: string) => Promise<void>; // Loads or creates the user's profile
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,

  /**
   * Sets the user in the auth store and triggers profile loading.
   * @param user - The authenticated user object.
   */
  setUser: (user) => {
    set({ user, isAuthenticated: !!user });
    if (user) {
      void get().loadUserProfile(user.id);
    }
  },

  /**
   * Loads the user's profile from Firestore or creates one if it doesn't exist.
   * @param userId - The ID of the user whose profile needs to be loaded.
   */
  loadUserProfile: async (userId: string) => {
    const addDebugMessage = useDebugStore.getState().addMessage;

    try {
      addDebugMessage("info", "Loading user profile");

      // Ensure the user's structure exists in Firestore
      await UserDatabaseStructure.ensureUserStructure(userId);

      // Reference to the user's profile document
      const profileRef = doc(
        db,
        UserDatabaseStructure.getProfilePath(userId),
        "data",
      );
      const profileDoc = await getDoc(profileRef);

      if (profileDoc.exists()) {
        // If the profile already exists, load it into the store
        const profileData = profileDoc.data() as unknown;
        const userData = isRecord(profileData) ? profileData : {};
        const role =
          typeof userData.role === "string" ? userData.role : undefined;
        const roles = Array.isArray(userData.roles)
          ? userData.roles.filter(
              (value): value is string => typeof value === "string",
            )
          : undefined;
        const email =
          typeof userData.email === "string" ? userData.email : undefined;

        console.log("🔍 Profile loaded from Firestore:", {
          role,
          roles,
          email,
        });
        useUserStore.getState().setProfile({
          ...(userData as Partial<UserProfile>),
          id: userId,
        } as UserProfile);

        // Validate vCard initialization
        const { isInitialized, missingVCards } =
          await VCardInitializationService.validateInitialization(userId);

        if (!isInitialized) {
          addDebugMessage(
            "warning",
            "Missing vCards detected, reinitializing",
            { missingVCards },
          );
          const { user } = get();
          if (!user) throw new Error("User not found");

          await VCardInitializationService.initializeUserVCards(
            userId,
            buildFallbackUserProfile(
              userId,
              user,
              userData as Partial<UserProfile>,
            ),
          );
        }

        // Validate SVG directories initialization
        const svgInitResult =
          await SVGInitializationService.validateInitialization(userId);
        if (!svgInitResult.isInitialized) {
          addDebugMessage(
            "warning",
            "Missing SVG directories detected, initializing",
            { missingDirectories: svgInitResult.missingDirectories },
          );
          await SVGInitializationService.initializeUserSVGDirectories(userId);
        }

        addDebugMessage("info", "User profile loaded successfully");
      } else {
        // If the profile does not exist, create a new one
        addDebugMessage("info", "Creating new user profile");
        const { user } = get();
        if (!user) throw new Error("User not found");

        const { firstName, lastName } = splitDisplayName(
          user.name || user.displayName,
        );

        // Create the profile in Firestore using userStore's createProfile function
        const createdProfile = await useUserStore
          .getState()
          .createProfile(userId, user.email || "", firstName, lastName);

        // Initialize vCards for the new user
        await VCardInitializationService.initializeUserVCards(
          userId,
          createdProfile,
        );

        // Initialize SVG directories for the new user
        await SVGInitializationService.initializeUserSVGDirectories(userId);

        addDebugMessage("info", "User profile and vCards initialized");
      }
    } catch (error) {
      addDebugMessage("error", "Error loading user profile", error);
      throw new Error(
        "Failed to load user profile: " +
          (error instanceof Error ? error.message : "Unknown error"),
      );
    }
  },

  /**
   * Logs the user out from Firebase and clears their profile from the store.
   */
  logout: async () => {
    try {
      // First sign out from Firebase
      await signOut(auth);

      // Then clear the profile
      useUserStore.getState().setProfile(null);

      // Finally clear auth state
      set({ user: null, isAuthenticated: false });
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  },
}));
